// Módulo de Metas e Vendas (PLANO 4.5).
//
// Todo o cálculo vive aqui, puro: recebe leads, agendamentos e a configuração
// do mês, devolve a grade diária, as taxas, o ranking e a projeção. É a parte
// do produto onde um erro silencioso vira decisão errada de gestão — por isso
// nada de Firestore, só entrada e saída testáveis.
//
// V5: o desempenho é por PESSOA, com o crédito dividido — leads trabalhados,
// agendamentos e comparecimentos ao SDR original; fechamentos e valor ao
// negociador (ver `credito.js`). Metas individuais nos quatro tipos.

import { creditoNegociador, creditoSdr, dataTrabalhado, ehVendedor } from '@/metas/credito';
import { conversaoDeValor, dataPerda, oportunidadePerdida, temDesfechoNegativo } from '@/leads/oportunidade';
// ★★ WK: a MESMA regra do dashboard e da comissão — uma função só (ver o módulo).
import {
  comparecimentosNoPeriodo,
  creditoComparecimento,
  indexarAgendamentosPorLead,
  indexarEquipe,
} from '@/leads/contagemComparecimento';
// ★★ WO lote 3a — a regra de "pendente" é IMPORTADA, nunca reescrita. `agenda/pendencia.js`
// nasceu no lote 2 com 19 casos e já está no ar; o painel diário usa a mesma.
import { contaNaMeta } from '@/agenda/pendencia';
import { EXPEDIENTE_PADRAO, expedienteDaPessoa, metaDoPeriodo } from '@/produtividade/expediente';
// ★ Os classificadores de interação também vêm prontos — tentativa e follow-up têm UMA
// definição na casa, a de `produtividade/dia.js`.
import { ETAPAS_DE_FOLLOW_UP, etapaDaInteracao } from '@/produtividade/dia';

/**
 * As metas de MÊS de uma pessoa. A ordem é a prioridade da "meta principal".
 *
 * ⚠⚠⚠ **A-2c (10/out/2026) — `agendamentos` e `comparecimentos` SAÍRAM DAQUI.** ★★★ Decisão do
 * dono: eles passaram a ser **por DIA**, como as ligações, e por isso **mudaram de nome**
 * (`agendamentosDia`, `comparecimentosDia`) — ver `METRICAS_PESSOA_DIA` e `METAS_ANTIGAS_DO_MES`.
 */
export const METRICAS_PESSOA = ['valor', 'leadsTrabalhados'];
export const METAS_PESSOA_VAZIA = { valor: 0, leadsTrabalhados: 0 };

/**
 * ★★★ A-2c — OS NOMES ANTIGOS, QUE NINGUÉM MAIS LÊ. **É esta lista que impede o "300 por dia".**
 *
 * ⚠⚠⚠ Em produção (medido em 10/out) **três pessoas têm `agendamentos: 300`** no documento de
 * `2026-10` — e 300 era **do MÊS**. ★★★ Se a conta nova lesse o mesmo campo como diário, a meta
 * delas viraria **300 por dia**: 6.600 no mês, e a tela mostraria todas em 2 % de atingimento.
 *
 * ★★ Por isso a transição é **por NOME NOVO, não por conversão**: ⚠ nenhum número é dividido,
 * estimado ou adivinhado — ★ o campo velho simplesmente **deixa de ser lido**, a tela mostra
 * **vazio** ("sem meta"), e o dono redigita o valor diário. ★★★ Dividir 300 por 22 daria 13,6, e
 * um número inventado numa meta é pior que nenhum: ⚠ ninguém sabe que ele foi inventado.
 *
 * ★ A lista existe como constante (e não como comentário) porque há **guarda de fonte** que
 * recusa qualquer leitura destes nomes fora daqui.
 */
export const METAS_ANTIGAS_DO_MES = ['agendamentos', 'comparecimentos'];

/**
 * ★★ WO lote 3a — AS METAS POR DIA. ★★★ A-2c: eram duas, agora são **quatro**.
 *
 * ⚠⚠ As de `METRICAS_PESSOA` são **do MÊS** ("R$ 200.000 em setembro"). Estas são **por DIA**
 * ("7 ligações por dia", "3 agendamentos por dia"). ★ Guardar as duas naturezas no mesmo mapa sem
 * separá-las é o caminho curto para o gestor digitar 150 onde cabia 7 — por isso a lista é
 * própria, o rótulo na tela diz a unidade, e a expansão para o período é feita em **UM** lugar
 * (`metaDoPeriodo`, que já desconta o sábado pela metade e ignora o domingo).
 */
export const METRICAS_PESSOA_DIA = ['ligacoesDia', 'followUpsDia', 'agendamentosDia', 'comparecimentosDia'];
export const METAS_PESSOA_DIA_VAZIA = {
  ligacoesDia: 0, followUpsDia: 0, agendamentosDia: 0, comparecimentosDia: 0,
};

/**
 * Qual métrica de REALIZADO cada meta diária cobra.
 *
 * ★ `agendados` e `comparecimentos` já eram contados por pessoa no `consolidarPeriodo` (é o que
 * alimentava o atingimento mensal) — ⚠ então a troca de unidade **não precisou de contagem nova**.
 */
export const REALIZADO_DA_META_DIA = {
  ligacoesDia: 'tentativas',
  followUpsDia: 'followUps',
  agendamentosDia: 'agendados',
  comparecimentosDia: 'comparecimentos',
};

/**
 * ★★★ A-2c — OS QUATRO CARTÕES DE "MINHA META", e de onde cada um sai AGORA.
 *
 * ⚠⚠ A tela continua mostrando os mesmos quatro — ★ mudou **a fonte** de dois deles:
 *
 * | cartão | fonte |
 * |---|---|
 * | `valor` | ★ meta do **MÊS**, digitada em reais |
 * | ★★★ `agendamentos` | ⚠ **meta por DIA** (`agendamentosDia`), somada pelo expediente |
 * | ★★★ `comparecimentos` | ⚠ **meta por DIA** (`comparecimentosDia`), idem |
 * | `leadsTrabalhados` | ★ meta do **MÊS** |
 *
 * ★★ A ordem é a prioridade da "meta principal" (a que dá o semáforo), e **não mudou** —
 * ⚠ mudar a ordem mudaria o semáforo de todo mundo sem ninguém pedir.
 */
export const CARTOES_DA_META = [
  { chave: 'valor', porDia: null },
  { chave: 'agendamentos', porDia: 'agendamentosDia' },
  { chave: 'comparecimentos', porDia: 'comparecimentosDia' },
  { chave: 'leadsTrabalhados', porDia: null },
];

/**
 * Normaliza `metasPorSdr[uid]` (aceita o formato antigo `{ valor }` e lixo).
 *
 * ⚠⚠ **A-2c: os campos antigos NÃO entram na saída.** ★★★ É aqui que o "300 por mês" morre —
 * ★ quem normaliza não os copia, então nenhuma conta adiante pode alcançá-los.
 */
export function normalizarMetasPessoa(obj) {
  const numero = (v) => Math.max(0, Number(String(v ?? '').replace(',', '.')) || 0);
  const saida = { ...METAS_PESSOA_VAZIA, ...METAS_PESSOA_DIA_VAZIA };
  for (const k of [...METRICAS_PESSOA, ...METRICAS_PESSOA_DIA]) saida[k] = numero(obj?.[k]);
  return saida;
}

/** Semáforo por atingimento: ≥100 verde, ≥70 amarelo, senão vermelho; sem meta → null. */
export const semaforoDe = (atingimento) => {
  if (atingimento === null || atingimento === undefined) return null;
  if (atingimento >= 100) return 'verde';
  if (atingimento >= 70) return 'amarelo';
  return 'vermelho';
};

export const METAS_PADRAO = {
  metaAgendamentoPct: 40,
  metaComparecimentoPct: 70,
  metaFechamentoPct: 30,
  ticketMedioAlvo: 0,
  metaValorVendas: 0,
  investimento: 0,
  modo: 'vendas', // 'vendas' | 'investimento'
  diasUteis: [1, 2, 3, 4, 5], // 0 = domingo
  metasPorSdr: {},
};

/** Converte Timestamp do Firestore, Date ou string em Date — ou null. */
export function paraData(valor) {
  if (!valor) return null;
  if (typeof valor.toDate === 'function') return valor.toDate();
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

/** Chave 'YYYY-MM-DD' no fuso local — a grade é lida por humanos, não por UTC. */
export function chaveDia(data) {
  const d = paraData(data);
  if (!d) return null;
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

export const chaveMes = (data) => chaveDia(data)?.slice(0, 7) ?? null;

/**
 * Os últimos N meses como chaves 'AAAA-MM', do atual para trás.
 *
 * ★ WG: estava DUPLICADO, byte a byte, em `RemuneracaoPage` e `MeuExtratoPage`.
 * A WG acrescentou o terceiro seletor (`/recebimentos`) e trouxe as três cópias
 * para cá, ao lado do `chaveMes` que elas já usavam — "o mesmo seletor" tem de
 * ser o mesmo código, senão um dia um deles muda de tamanho sozinho.
 */
export const mesesRecentes = (quantos = 12) => {
  const hoje = new Date();
  return Array.from({ length: quantos }, (_, i) =>
    chaveMes(new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)));
};

/**
 * ★★ WG-c1 (28/set/2026) — O PISO DE MÊS DA EQUIPE.
 *
 * Do que o dono pediu, na palavra dele: *"Na visão Meu extrato das SDRs, ocultar
 * os fechamentos dos meses anteriores. Porque pode ser que tenha inconsistências,
 * como a gente não fechou nos meses anteriores via sistema, e aí começam a gerar
 * dúvidas sobre os valores."*
 *
 * ★ MEDIDO em 28/set: `clinicas/{id}/fechamentos` tem **ZERO** documentos —
 * nenhum mês foi fechado pelo sistema, nunca. O extrato de agosto para trás é
 * uma PRÉVIA recalculada no navegador sobre dados que ninguém conferiu; mostrar
 * isso a quem ganha comissão convida a uma dúvida que não temos como responder.
 *
 * ★ O GESTOR NÃO TEM PISO — ele precisa olhar para trás para conferir. Quem
 * decide o piso por papel é a página; estas funções só sabem de meses, e é de
 * propósito: regra de mês não deve conhecer papel.
 *
 * ★ Constante, e num lugar só: a string '2026-09' não se espalha pelo código.
 * O dia em que um mês for fechado de verdade, é aqui que se mexe.
 */
export const PISO_MES_DA_EQUIPE = '2026-09';

/**
 * Os meses de uma lista que estão a partir do piso (inclusive).
 *
 * ★ É `>=`, não `>`: o próprio mês do piso TEM de aparecer — senão setembro/2026
 * sumiria da tela justamente no mês em que a regra entrou. Sem piso, devolve a
 * lista inteira (uma CÓPIA, para ninguém mexer na de quem chamou).
 *
 * A comparação é de TEXTO, e funciona porque 'AAAA-MM' tem o mês com dois
 * dígitos: '2026-09' < '2026-10' e '2025-12' < '2026-01'.
 */
export const mesesDesde = (meses = [], piso = null) =>
  (piso ? (meses ?? []).filter((m) => m >= piso) : [...(meses ?? [])]);

/**
 * Os meses que uma pessoa pode ESCOLHER.
 *
 * ★ Função separada, e não um parâmetro novo no `mesesRecentes`: aquele tem três
 * consumidores, e dois deles (`/recebimentos` e `/remuneracao`) são telas do
 * gestor, onde o piso NÃO pode valer. Um parâmetro opcional ali convidaria ao
 * padrão errado — o nome explícito na chamada é o que impede isso.
 */
export const mesesVisiveis = (quantos = 12, { piso = null } = {}) =>
  mesesDesde(mesesRecentes(quantos), piso);

/**
 * O mês que de fato vale, dado o piso — nunca abaixo dele.
 *
 * ★★ ISTO É O QUE FAZ O PISO VALER NA LEITURA, e não só no seletor. A página
 * deriva o mês efetivo e o usa em TODAS as consultas (agendamentos, recebimentos,
 * snapshot) e no cálculo da prévia. Guardar um mês inválido no estado e corrigi-lo
 * depois deixaria uma janela de um render em que os números do mês proibido
 * apareceriam; derivar não deixa janela nenhuma.
 *
 * ★ Mês vazio ou ausente NÃO vira o piso: devolver um mês plausível esconderia
 * um defeito de quem chamou atrás de um número que parece certo.
 */
export const mesPermitido = (mes, { piso = null } = {}) =>
  (piso && mes && mes < piso ? piso : mes);

/** Primeiro e último instante do mês 'YYYY-MM'. */
export function limitesDoMes(mes) {
  const [ano, m] = mes.split('-').map(Number);
  return {
    inicio: new Date(ano, m - 1, 1, 0, 0, 0, 0),
    fim: new Date(ano, m, 0, 23, 59, 59, 999),
  };
}

/** Todos os dias do mês, com a marcação de dia útil conforme a clínica. */
export function diasDoMes(mes, diasUteis = METAS_PADRAO.diasUteis) {
  const { inicio, fim } = limitesDoMes(mes);
  const dias = [];
  for (let d = new Date(inicio); d <= fim; d.setDate(d.getDate() + 1)) {
    const data = new Date(d);
    dias.push({
      chave: chaveDia(data),
      data,
      diaSemana: data.getDay(),
      util: diasUteis.includes(data.getDay()),
    });
  }
  return dias;
}

const soma = (lista, campo) => lista.reduce((t, x) => t + (Number(x[campo]) || 0), 0);
export const pct = (parte, total) => (total > 0 ? (parte / total) * 100 : 0);

/**
 * As taxas encadeadas como a SDR lê: cada etapa sobre a anterior, não sobre o
 * total. Compartilhado entre o painel de metas e o dashboard para que os dois
 * nunca contem de jeitos diferentes.
 */
export function taxasDoFunil({ leads = 0, agendados = 0, comparecimentos = 0, fechamentos = 0, valor = 0 }) {
  return {
    agendamento: pct(agendados, leads),
    comparecimento: pct(comparecimentos, agendados),
    fechamento: pct(fechamentos, comparecimentos),
    ticketMedio: fechamentos > 0 ? valor / fechamentos : 0,
  };
}

/**
 * Data em que o lead foi fechado. Leads antigos ou importados podem não ter
 * dataFechamento — nesse caso a última atualização é a melhor aproximação
 * disponível, e é melhor contá-los do que sumir com a receita do relatório.
 */
export const dataFechamento = (lead) => paraData(lead.dataFechamento) ?? paraData(lead.atualizadoEm);

/**
 * ★ VD — Consolida um PERÍODO qualquer: os recortes, os totais, as taxas e o
 * desempenho por pessoa. É a calculadora ÚNICA; `consolidarMes` a envolve e
 * acrescenta só o que é de mês (grade diária, projeção, ROI, atingimento).
 *
 * Nasceu por extração, sem mudar número nenhum — a prova é
 * `consolidarMes.igualdade.test.js`, gravado ANTES da extração. Se a saída de
 * `consolidarMes` mudar um centavo, aquele teste cai.
 *
 * `ritmo` ({ uteisDecorridos, totalDiasUteis }) é o que a projeção por pessoa
 * precisa, e é conceito de mês: sem ele, a projeção fica 0. Foi mantido como
 * parâmetro (e não recomputado aqui) exatamente para o mês continuar idêntico.
 *
 * @param inicio, fim  a janela, fechada nos dois lados
 */
export function consolidarPeriodo({
  inicio,
  fim,
  leads = [],
  agendamentos = [],
  metas = {},
  equipe = [],
  ritmo = null,
  // ★★ WO lote 3a — três entradas novas, todas OPCIONAIS.
  interacoes = [],
  expediente = EXPEDIENTE_PADRAO,
  // ⚠ `agora` entra por PARÂMETRO (regra da WN): "pendente" depende do relógio, e função
  // que o lê por dentro não é testável em instante nenhum.
  agora = new Date(),
  // ★ 02/11/2026 — feriados sem meta. Opcional, padrão `[]`: o golden não muda. ⚠ Só as telas
  // desligadas (/metas, /placar) chegam aqui; quem as religar tem de passar a lista
  // (guarda em `produtividade/feriado.guardas.test.js`).
  feriados = [],
}) {
  const config = { ...METAS_PADRAO, ...metas };
  const dentro = (data) => data && data >= inicio && data <= fim;

  // ---- recortes do período ----
  const leadsCriados = leads.filter((l) => dentro(paraData(l.criadoEm)));
  /**
   * ★★★ WO lote 3a — D2: AGENDAMENTO PENDENTE NÃO CONTA NA META.
   *
   * Conta quem teve **baixa** (compareceu ou faltou) ou cuja **data ainda está por vir**.
   * ⚠ Fica de fora o que venceu e ninguém resolveu — o "cria e nunca resolve", que era a
   * forma mais fácil de inflar o indicador.
   *
   * ★ A regra NÃO é escrita aqui: `contaNaMeta` vem de `agenda/pendencia.js`, com 19 casos
   * de teste e já no ar desde o lote 2, usada também pelo painel diário. Reescrevê-la seria
   * a terceira definição de alguma coisa nesta casa.
   *
   * ⚠⚠ ISTO MUDA NÚMEROS JÁ PUBLICADOS, de propósito: medido em setembro, **183 → 98**
   * (87 com baixa + 11 futuros). Jéssica **51 → 23**, Talita **21 → 11**. O golden
   * `consolidarMes.igualdade.test.js` fica vermelho porque a mudança chegou onde devia.
   */
  const marcados = agendamentos.filter(
    (a) => dentro(paraData(a.criadoEm)) && contaNaMeta(a, agora),
  );
  /** ★ O que o D2 tirou — informação, não punição. A tela pode mostrar o quanto caiu. */
  const marcadosPendentes = agendamentos.filter(
    (a) => dentro(paraData(a.criadoEm)) && !contaNaMeta(a, agora),
  );
  /**
   * ★★ WK (29/set/2026) — COMPARECIMENTO POR PESSOA (D1).
   *
   * Era `agendamentos.filter(status === 'compareceu' && dentro(dataHora))` — por
   * AGENDAMENTO. Passou a ser UM POR LEAD, pela mesma função que o dashboard e a
   * comissão usam (`@/leads/comparecimento`), que por sua vez reusa a
   * `dataComparecimento` já espelhada contra o servidor.
   *
   * ⚠ O número MUDA nos dois sentidos, e foi medido antes: setembro 55 → 62
   * (entram os que têm `compareceuEm` sem agendamento); **agosto 138 → 126**
   * (paciente que voltou no mesmo mês deixa de contar duas vezes).
   *
   * ★ A saída é `[{ lead, quando }]`, e não agendamentos: quem credita pessoa
   * abaixo usa `lead` direto, sem precisar reencontrá-lo pelo `leadId`.
   */
  const compareceram = comparecimentosNoPeriodo(leads, agendamentos, dentro);
  const fechados = leads.filter((l) => l.etapa === 'fechado' && dentro(dataFechamento(l)));
  // VE — recortes da oportunidade.
  const orcadosNoMes = leads.filter(
    (l) => (Number(l.valorOrcado) || 0) > 0 && dentro(paraData(l.valorOrcadoEm)),
  );
  // VG: "perdido" é motivo que encerra, lido pelo carimbo `desfechoNegativoEm`.
  const perdidosNoMes = leads.filter((l) => temDesfechoNegativo(l) && dentro(dataPerda(l)));

  // ---- totais e taxas ----
  const valorVendido = soma(fechados, 'valorFechado');
  const totais = {
    leads: leadsCriados.length,
    agendados: marcados.length,
    comparecimentos: compareceram.length,
    fechamentos: fechados.length,
    valor: valorVendido,
    // VE: o orçado do período e a entrada paga no ato dos contratos do período.
    orcado: soma(orcadosNoMes, 'valorOrcado'),
    entrada: soma(fechados, 'valorEntrada'),
  };

  /**
   * VE — conversão de valor e oportunidade perdida, sobre os leads com DESFECHO
   * no período (fechados por `dataFechamento`, perdidos por `perdidoEm`).
   */
  const comDesfecho = [...fechados, ...perdidosNoMes];
  const conversaoValor = conversaoDeValor(comDesfecho);
  const perdaDeOportunidade = oportunidadePerdida(perdidosNoMes, agendamentos);

  const taxas = taxasDoFunil(totais);

  // ---- desempenho por PESSOA (V5): crédito SDR × negociador ----
  //
  // "Leads trabalhados" contam pelo período da PRIMEIRA ATRIBUIÇÃO
  // (`sdrOriginalEm`), não pela criação: o lead do resgate nasce sem SDR e é
  // distribuído depois. Sem o campo (lead antigo), vale `criadoEm`.
  const trabalhados = leads.filter((l) => dentro(dataTrabalhado(l)));
  const leadPorId = new Map(leads.map((l) => [l.id, l]));

  const porPessoa = new Map();
  const garantir = ({ id, nome }) => {
    if (!porPessoa.has(id)) {
      porPessoa.set(id, {
        id, nome, leads: 0, agendados: 0, comparecimentos: 0, fechamentos: 0, valor: 0,
        // ★ WO lote 3a — só saem de zero quando `interacoes` é passada.
        tentativas: 0, followUps: 0,
      });
    }
    return porPessoa.get(id);
  };
  for (const u of equipe) if (ehVendedor(u)) garantir({ id: u.id, nome: u.nome });

  /**
   * ★★ WO lote 3a — TENTATIVAS (leads distintos) e FOLLOW-UPS, por pessoa.
   *
   * ⚠ `interacoes` é opcional: quem não as carrega (o Placar, por exemplo) recebe zero, e
   * as metas por dia simplesmente não têm realizado para cobrar. ★ Melhor zero declarado do
   * que a tela inventar um número.
   *
   * ★★ A unidade é **lead distinto**, não clique — a mesma de `painelDoDia`. É o que torna
   * clicar duas vezes no mesmo lead inofensivo **sem precisar de trava de cadência**.
   *
   * ★ O autor tem de ser da EQUIPE. Medido em produção: das 2.552 interações desde 01/09,
   * 1.503 têm autor que não é gente (`sistema` 1.006, importação 340, migração 157). Sem o
   * filtro, três fantasmas entrariam no ranking.
   */
  const idsDaEquipe = new Set((equipe ?? []).map((u) => u?.id).filter(Boolean));
  const tentadosPorPessoa = new Map();
  const followUpsPorPessoa = new Map();
  for (const i of interacoes ?? []) {
    if (!i?.autorId || !idsDaEquipe.has(i.autorId)) continue;
    if (!dentro(paraData(i.criadoEm))) continue;
    const pessoa = garantir({ id: i.autorId, nome: i.autorNome ?? '' });
    if (i.tipo === 'ligacao' && i.leadId) {
      if (!tentadosPorPessoa.has(pessoa.id)) tentadosPorPessoa.set(pessoa.id, new Set());
      tentadosPorPessoa.get(pessoa.id).add(i.leadId);
    }
    const { etapa } = etapaDaInteracao(i, leadPorId.get(i.leadId));
    if (ETAPAS_DE_FOLLOW_UP.includes(etapa)) {
      followUpsPorPessoa.set(pessoa.id, (followUpsPorPessoa.get(pessoa.id) ?? 0) + 1);
    }
  }
  for (const [id, leadsTentados] of tentadosPorPessoa) porPessoa.get(id).tentativas = leadsTentados.size;
  for (const [id, n] of followUpsPorPessoa) porPessoa.get(id).followUps = n;
  for (const l of trabalhados) garantir(creditoSdr(l)).leads += 1;
  /**
   * ★★★ WO lote 3b — O AGENDAMENTO CREDITA QUEM MARCOU, não o SDR do lead.
   *
   * ⚠⚠ MUDANÇA DE NÚMERO PUBLICADO, decidida pelo dono em 3/out, e grande.
   *
   * Até aqui esta linha era `garantir(creditoSdr(lead)).agendados += 1` — o crédito ia para
   * o SDR do LEAD. ★ Medido em setembro, a mesma pessoa aparecia com **dois** números:
   * **20** nos Indicadores (que sempre contaram `agendadoPorId`) e **96** aqui. ⚠ E o 96 é
   * quase inútil como medida de trabalho: a Jéssica é a `sdrOriginal` de **7.834 dos 7.964**
   * leads, então "agendamentos dos leads dela" é praticamente "todos da clínica".
   *
   * ★ Agora as duas telas respondem à MESMA pergunta — *"quem apertou o botão"* —, e o
   * rótulo que avisava da divergência sai junto.
   *
   * ⚠ O que NÃO muda: `comparecimentos` e `leads` continuam creditando ao SDR do lead. Só o
   * AGENDAMENTO tem autor próprio (`agendadoPorId`); comparecimento é do lead, e a regra
   * única da WK é quem decide.
   *
   * ⚠⚠ E a comissão NÃO muda: a folha lê `compareceuEm` e `recebimentos`, e nenhum dos dois
   * passa por aqui. Duas guardas de grafo provam.
   */
  for (const a of marcados) {
    if (!a?.agendadoPorId) continue;   // ★ sem autor, não credita ninguém (22 em setembro)
    garantir({ id: a.agendadoPorId, nome: a.agendadoPorNome ?? '' }).agendados += 1;
  }
  /**
   * ★★★ A-10.4 (16/out/2026) — O COMPARECIMENTO CREDITA QUEM AGENDOU.
   *
   * ⚠⚠ Era `garantir(creditoSdr(lead)).comparecimentos` — o SDR original do lead.
   * ★ Decisão do dono: o crédito é de quem criou o agendamento em que o paciente
   * compareceu. ⚠⚠⚠ Isto muda a FOLHA, e por isso a mesma função serve
   * `remuneracao/calculo.js` e o espelho do servidor: uma régua só.
   *
   * ★ `agendamentos` do lead vêm de `comparecimentosNoPeriodo`, que já os indexou.
   */
  const daEquipeParaComparecimento = indexarEquipe(equipe);
  const agsPorLeadParaComparecimento = indexarAgendamentosPorLead(agendamentos);
  for (const { lead } of compareceram) {
    const doLead = agsPorLeadParaComparecimento.get(lead?.id) ?? [];
    garantir(creditoComparecimento(lead, doLead, daEquipeParaComparecimento)).comparecimentos += 1;
  }
  for (const l of fechados) {
    const linha = garantir(creditoNegociador(l));
    linha.fechamentos += 1;
    linha.valor += Number(l.valorFechado) || 0;
  }

  // Sem meta individual de VALOR, divide a meta da clínica igualmente entre as
  // pessoas. As outras três metas não têm padrão: sem valor configurado, não há
  // meta (atingimento null) — inventar uma escondia a ausência.
  const pessoas = [...porPessoa.values()].filter((p) => p.id);
  const metaPadraoValor = pessoas.length > 0 ? config.metaValorVendas / pessoas.length : 0;
  const uteisDecorridos = ritmo?.uteisDecorridos ?? 0;
  const totalDiasUteis = ritmo?.totalDiasUteis ?? 0;

  const ranking = [...porPessoa.values()]
    .map((p) => {
      const metasDaPessoa = normalizarMetasPessoa(config.metasPorSdr?.[p.id]);
      if (!metasDaPessoa.valor && p.id) metasDaPessoa.valor = metaPadraoValor;
      const realizado = {
        valor: p.valor,
        agendamentos: p.agendados,
        comparecimentos: p.comparecimentos,
        leadsTrabalhados: p.leads,
      };
      const atingimentos = {};
      const projecaoDaPessoa = {};
      for (const k of METRICAS_PESSOA) {
        atingimentos[k] = metasDaPessoa[k] > 0 ? (realizado[k] / metasDaPessoa[k]) * 100 : null;
        projecaoDaPessoa[k] = uteisDecorridos > 0 ? (realizado[k] / uteisDecorridos) * totalDiasUteis : 0;
      }
      // ⚠ A projeção dos dois cartões migrados é calculada aqui também: ★ ela é do REALIZADO
      // (não da meta), e por isso independe da unidade em que a meta foi digitada.
      for (const { chave, porDia } of CARTOES_DA_META) {
        if (!porDia) continue;
        projecaoDaPessoa[chave] = uteisDecorridos > 0 ? (realizado[chave] / uteisDecorridos) * totalDiasUteis : 0;
      }
      /**
       * ★★ AS METAS POR DIA viram meta DO PERÍODO aqui — e só aqui.
       *
       * `metaDoPeriodo` soma `metaDoDia` sobre os dias úteis da janela, com o **sábado
       * valendo metade, arredondado para baixo**. ⚠ Não é `meta × nº de dias`: um mês com 5
       * sábados tem meta menor que um com 4, e é isso que torna a comparação entre meses
       * honesta.
       */
      /**
       * ★★★ A-2 — O EXPEDIENTE É DA PESSOA, e aqui ele precisa de uma BUSCA.
       *
       * ⚠⚠ `p` é um acumulador de contagens (`garantir({id, nome})`), **não** o documento da
       * pessoa — ★ ele nunca teve o campo `expediente`. ★★ O documento está em `equipe`, que
       * esta função já recebe. ⚠ Sem este `find`, a meta de semana/mês da Talita continuaria
       * sendo calculada com o expediente da clínica, e **só o cartão do dia** teria sido
       * consertado — o pior dos mundos, porque os dois números apareceriam na mesma tela.
       */
      const doBanco = (equipe ?? []).find((u) => u?.id === p.id);
      const expedienteDaLinha = expedienteDaPessoa(doBanco, expediente);
      const metasNoPeriodo = {};
      for (const k of METRICAS_PESSOA_DIA) {
        const diaria = metasDaPessoa[k];
        const alvo = diaria > 0 ? metaDoPeriodo(diaria, inicio, fim, expedienteDaLinha, feriados) : 0;
        const feito = p[REALIZADO_DA_META_DIA[k]] ?? 0;
        metasNoPeriodo[k] = {
          diaria,
          noPeriodo: alvo,
          realizado: feito,
          // ⚠ `null` sem meta, nunca 0%: zero diria que a pessoa falhou num alvo inexistente.
          atingimento: alvo > 0 ? (feito / alvo) * 100 : null,
        };
      }
      /**
       * ★★★ A-2c — OS DOIS CARTÕES MIGRADOS GANHAM `metas`/`atingimentos` **derivados do
       * valor DIÁRIO**, pela mesma função das ligações (`metaDoPeriodo`).
       *
       * ⚠⚠ Por que derivar em vez de mudar a tela: ★ `metas.agendamentos` continua querendo
       * dizer *"a meta de agendamentos deste período"* — ★★ é o que o cartão mostra. O que
       * mudou é **de onde o número vem**: antes era digitado por mês; ⚠⚠⚠ agora é
       * `metaDiária × dias do período`, com **sábado meio e domingo zero**, pelo expediente
       * **da pessoa**.
       *
       * ★ E é o que mantém `temMetaPropria`, `MinhaMeta`, o Dashboard e o Placar funcionando
       * sem cada um refazer a conta — ⚠ quatro contas iguais em quatro lugares divergem.
       */
      for (const { chave, porDia } of CARTOES_DA_META) {
        if (!porDia) continue;
        const doDia = metasNoPeriodo[porDia];
        metasDaPessoa[chave] = doDia.noPeriodo;
        atingimentos[chave] = doDia.atingimento;
      }

      const metaPrincipal = CARTOES_DA_META.map((c) => c.chave).find((k) => metasDaPessoa[k] > 0) ?? null;
      return {
        ...p,
        // Compatibilidade com quem lia o ranking antigo (valor é a meta "clássica").
        sdrId: p.id,
        meta: metasDaPessoa.valor,
        atingimento: metasDaPessoa.valor > 0 ? (p.valor / metasDaPessoa.valor) * 100 : 0,
        // ⚠ As CHAVES de saída continuam `metas` e `projecao` — quem consome o
        // ranking não muda. O que mudou foi só o nome das variáveis locais, que
        // sombreavam nomes do escopo de cima.
        metas: metasDaPessoa,
        realizado,
        atingimentos,
        projecao: projecaoDaPessoa,
        metaPrincipal,
        semaforo: metaPrincipal ? semaforoDe(atingimentos[metaPrincipal]) : null,
        // ★ WO lote 3a — chave NOVA, ao lado das antigas: quem já lia o ranking não muda.
        metasPorDia: metasNoPeriodo,
      };
    })
    .sort((a, b) => b.valor - a.valor || b.leads - a.leads);

  return {
    inicio,
    fim,
    config,
    // Os recortes voltam para quem precisa distribuí-los (a grade diária do
    // mês) não ter de recortar de novo por outra regra.
    // ★ `marcadosPendentes` é o que o D2 tirou da conta — informação, não punição.
    recortes: { leadsCriados, marcados, marcadosPendentes, compareceram, fechados, orcados: orcadosNoMes, perdidos: perdidosNoMes },
    totais,
    taxas,
    conversaoValor,
    oportunidadePerdida: perdaDeOportunidade,
    ranking,
  };
}

/**
 * Consolida o mês.
 *
 * Desde a VD é um envelope de `consolidarPeriodo` com a janela do mês, mais o
 * que só faz sentido em mês: grade diária, meta diária, projeção pelo ritmo dos
 * dias úteis, ROI e atingimento das metas da clínica. ⚠ A forma do objeto
 * devolvido é EXATAMENTE a de antes — chave a mais ou a menos derruba a prova
 * de igualdade.
 *
 * @param mes           'YYYY-MM'
 * @param leads         todos os leads da clínica
 * @param agendamentos  todos os agendamentos da clínica
 * @param metas         configuração do mês (ver METAS_PADRAO)
 * @param equipe        usuários, para o ranking
 * @param agora         referência de "hoje", injetável para teste
 */
export function consolidarMes({
  mes,
  leads = [],
  agendamentos = [],
  metas = {},
  equipe = [],
  agora = new Date(),
  // ★ WO lote 3a — repassados para `consolidarPeriodo`; opcionais, como lá.
  interacoes = [],
  expediente = EXPEDIENTE_PADRAO,
  feriados = [],
}) {
  const { inicio, fim } = limitesDoMes(mes);
  const configMes = { ...METAS_PADRAO, ...metas };

  // ---- o ritmo do mês, que a projeção por pessoa precisa ----
  const dias = diasDoMes(mes, configMes.diasUteis);
  const totalDiasUteis = dias.filter((d) => d.util).length;
  const chaveHoje = chaveDia(agora);
  const uteisDecorridos = dias.filter((d) => d.util && d.chave <= chaveHoje).length;

  const periodo = consolidarPeriodo({
    inicio, fim, leads, agendamentos, metas, equipe,
    ritmo: { uteisDecorridos, totalDiasUteis },
    // ⚠ `agora` desce junto: é o D2 que depende dele, e deixá-lo para trás faria o mês
    // usar o relógio real enquanto o teste fixava o seu.
    interacoes, expediente, agora, feriados,
  });
  const { config, recortes, totais, taxas, conversaoValor, oportunidadePerdida: perda, ranking } = periodo;
  const { leadsCriados, marcados, compareceram, fechados, orcados } = recortes;

  // ---- grade diária ----
  const vazio = () => ({ leads: 0, agendados: 0, comparecimentos: 0, fechamentos: 0, valor: 0, orcado: 0, entrada: 0 });
  const porDia = new Map(dias.map((d) => [d.chave, vazio()]));
  const acumular = (chave, campo, quanto = 1) => {
    const linha = porDia.get(chave);
    if (linha) linha[campo] += quanto;
  };

  for (const l of leadsCriados) acumular(chaveDia(l.criadoEm), 'leads');
  for (const a of marcados) acumular(chaveDia(a.criadoEm), 'agendados');
  // ★ WK: `compareceram` virou `[{ lead, quando }]` (um por pessoa). O dia é o
  // `quando` da regra única — que é `compareceuEm` quando existe, e não mais a
  // `dataHora` de um agendamento que pode nem existir.
  for (const { quando } of compareceram) acumular(chaveDia(quando), 'comparecimentos');
  for (const l of fechados) {
    const chave = chaveDia(dataFechamento(l));
    acumular(chave, 'fechamentos');
    acumular(chave, 'valor', Number(l.valorFechado) || 0);
    // VE: a entrada é paga NO ATO do fechamento — mesmo dia do contrato.
    acumular(chave, 'entrada', Number(l.valorEntrada) || 0);
  }
  // VE: o orçado entra no dia em que foi ORÇADO, não no da criação nem no do
  // fechamento — é quando a oportunidade nasceu, e é o que a grade mostra.
  for (const l of orcados) acumular(chaveDia(l.valorOrcadoEm), 'orcado', Number(l.valorOrcado) || 0);

  const metaDiaria = totalDiasUteis > 0 ? config.metaValorVendas / totalDiasUteis : 0;
  const grade = dias.map((d) => {
    const linha = porDia.get(d.chave) ?? vazio();
    const passado = d.chave <= chaveHoje;
    return {
      ...d,
      ...linha,
      metaDiaria: d.util ? metaDiaria : 0,
      // Semáforo só faz sentido em dia útil já vivido: dia futuro não está
      // "abaixo da meta", ainda não aconteceu.
      semaforo: !d.util || !passado ? null : linha.valor >= metaDiaria ? 'verde' : 'vermelho',
    };
  });

  // ---- projeção pelo ritmo dos dias úteis já vividos ----
  const valorVendido = totais.valor;
  const projecao =
    uteisDecorridos > 0 ? (valorVendido / uteisDecorridos) * totalDiasUteis : 0;

  // ---- ROI, quando a clínica trabalha por investimento ----
  const investimento = Number(config.investimento) || 0;
  const roi =
    config.modo === 'investimento' && investimento > 0
      ? {
          investimento,
          custoPorLead: totais.leads > 0 ? investimento / totais.leads : 0,
          custoPorFechamento: totais.fechamentos > 0 ? investimento / totais.fechamentos : 0,
          retorno: valorVendido / investimento,
        }
      : null;

  return {
    mes,
    config,
    grade,
    totais,
    taxas,
    metaDiaria,
    totalDiasUteis,
    uteisDecorridos,
    projecao,
    roi,
    ranking,
    conversaoValor,
    oportunidadePerdida: perda,
    // Comparação direta com o que a clínica prometeu entregar.
    atingimento: {
      valor: config.metaValorVendas > 0 ? (valorVendido / config.metaValorVendas) * 100 : 0,
      agendamento: config.metaAgendamentoPct > 0
        ? (taxas.agendamento / config.metaAgendamentoPct) * 100
        : 0,
      comparecimento: config.metaComparecimentoPct > 0
        ? (taxas.comparecimento / config.metaComparecimentoPct) * 100
        : 0,
      fechamento: config.metaFechamentoPct > 0
        ? (taxas.fechamento / config.metaFechamentoPct) * 100
        : 0,
      ticketMedio: config.ticketMedioAlvo > 0
        ? (taxas.ticketMedio / config.ticketMedioAlvo) * 100
        : 0,
    },
  };
}

/**
 * A linha do desempenho por pessoa de um usuário — a "Minha meta" (V5).
 * `null` para quem não tem linha (recepção, superadmin, uid desconhecido).
 */
export function minhaMeta(resumo, uid) {
  if (!uid || !resumo?.ranking) return null;
  return resumo.ranking.find((p) => p.id === uid) ?? null;
}
